import React, { Component, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
    fetchEcomLinks,
    createEcomLink,
    regenerateEcomLinkCode,
    detachEcomLink,
} from '../../../../../../jsx/actions/settingsAction';
import {
    CODE_VALIDITY_MS,
    canManageEcomLinks,
    canSetLegacyStoreId,
    isValidLegacyStoreId,
    stateLabel,
    isSyncLate,
    remainingMs,
    formatCountdown,
    ecomErrorMessage,
} from '../../../../../../jsx/utils/ecomLinks';

// Rafraîchissement de la liste (état de synchro remonté par le connecteur).
const REFRESH_MS = 30 * 1000;

const STATE_COLORS = {
    active: { backgroundColor: '#e8f5e9', color: '#2e7d32' },
    pairing: { backgroundColor: '#fff8e1', color: '#f57f17' },
    pending: { backgroundColor: '#e3f2fd', color: '#1565c0' },
    detached: { backgroundColor: '#eeeeee', color: '#616161' },
};

function formatDate(iso) {
    const t = Date.parse(iso);
    return Number.isNaN(t) ? '—' : new Date(t).toLocaleString('fr-FR');
}

/**
 * Code de jumelage : affiché une seule fois, avec Copier et un compte à rebours de 30 minutes.
 * Le code vit uniquement dans l'état du parent (jamais dans Redux) et disparaît à la fermeture.
 */
function CodeView({ code, expiresAt }) {
    const [now, setNow] = useState(Date.now());
    const [copied, setCopied] = useState(false);
    // Sans expires_at dans la réponse, on compte 30 minutes à partir de l'affichage.
    const [fallbackEnd] = useState(Date.now() + CODE_VALIDITY_MS);

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    const left = expiresAt ? remainingMs(expiresAt, now) : Math.max(0, fallbackEnd - now);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
        } catch (e) {
            setCopied(false);
        }
    };

    return (
        <Box>
            <Alert severity="warning" style={{ marginBottom: 12 }}>
                Ce code n&apos;est affiché qu&apos;une seule fois. Copiez-le maintenant et collez-le dans le module
                MyJouleBox Connect de la boutique PrestaShop.
            </Alert>
            <TextField
                fullWidth
                variant="outlined"
                value={code}
                InputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: 13 } }}
                onFocus={(e) => e.target.select()}
            />
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
                <Typography variant="body2" color={left > 0 ? 'textSecondary' : 'error'}>
                    {left > 0 ? 'Valable encore ' + formatCountdown(left) : 'Code expiré : générez-en un nouveau.'}
                </Typography>
                <Button size="small" variant="outlined" onClick={copy} disabled={left <= 0}>
                    {copied ? 'Copié' : 'Copier'}
                </Button>
            </Box>
        </Box>
    );
}

class EcomStoreLinksForm extends Component {
    state = {
        // Dialogue de création
        createOpen: false,
        shops: [],
        shopsError: null,
        shopRef: '',
        legacyStoreId: '',
        // Code affiché (création ou régénération) : { code, expiresAt, title }
        codeShown: null,
        // Détachement à confirmer : lien
        detachTarget: null,
        actionError: null,
    };

    refreshTimer = null;

    componentDidMount() {
        this.props.fetchEcomLinks();
        this.refreshTimer = setInterval(this.props.fetchEcomLinks, REFRESH_MS);
    }

    componentWillUnmount() {
        clearInterval(this.refreshTimer);
    }

    loadShops = async () => {
        try {
            // /getList/other relaie storehouse /api/account/shops (magasins du compte) ;
            // /getList relaie /api/transfers/{page}, qui ne liste pas les magasins.
            const response = await fetch('/stocks/getList/other', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({ page: 'shops' }),
            });
            const list = await response.json();
            if (!Array.isArray(list)) {
                this.setState({ shopsError: 'Impossible de charger les magasins' });
                return;
            }
            this.setState({
                shops: list.filter((s) => s && s.shop_ref && !s.shop_disabled),
                shopsError: null,
            });
        } catch (e) {
            this.setState({ shopsError: 'Impossible de charger les magasins' });
        }
    };

    openCreate = () => {
        this.setState({ createOpen: true, shopRef: '', legacyStoreId: '', actionError: null });
        this.loadShops();
    };

    closeCreate = () => this.setState({ createOpen: false });

    handleCreate = async () => {
        const { shopRef, legacyStoreId } = this.state;
        const legacy = canSetLegacyStoreId(this.props.userRole) ? legacyStoreId.trim() : '';
        const result = await this.props.createEcomLink(shopRef, legacy);
        if (!result.ok) {
            this.setState({ actionError: ecomErrorMessage(result.status, result.body) });
            return;
        }
        const shop = this.state.shops.find((s) => s.shop_ref === shopRef);
        this.setState({
            createOpen: false,
            actionError: null,
            codeShown: {
                code: result.data.code,
                expiresAt: result.data.expires_at,
                title: 'Code de jumelage' + (shop ? ' — ' + shop.name : ''),
            },
        });
    };

    handleRegenerate = async (link) => {
        const result = await this.props.regenerateEcomLinkCode(link.store_id);
        if (!result.ok) {
            this.setState({ actionError: ecomErrorMessage(result.status, result.body) });
            return;
        }
        this.setState({
            actionError: null,
            codeShown: {
                code: result.data.code,
                expiresAt: result.data.expires_at,
                title: 'Nouveau code — ' + (link.shop_name || link.store_id),
            },
        });
    };

    handleDetach = async () => {
        const link = this.state.detachTarget;
        const result = await this.props.detachEcomLink(link.store_id);
        this.setState({
            detachTarget: null,
            actionError: result.ok ? null : ecomErrorMessage(result.status, result.body),
        });
    };

    renderSync(link) {
        const sync = link.sync || {};
        if (link.state !== 'active') {
            return <Typography variant="body2" color="textSecondary">—</Typography>;
        }
        return (
            <Box>
                <Typography variant="body2">
                    Stock : {formatDate(sync.last_stock_sync_at)}
                    {isSyncLate(link) && (
                        <Chip label="En retard" size="small" style={{ marginLeft: 8, backgroundColor: '#fff3e0', color: '#e65100' }} />
                    )}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Dernière commande : {formatDate(sync.last_order_at)}
                </Typography>
                {sync.last_error && (
                    <Typography variant="body2" color="error" style={{ wordBreak: 'break-word' }}>
                        Erreur ({formatDate(sync.last_error_at)}) : {sync.last_error}
                    </Typography>
                )}
            </Box>
        );
    }

    renderLinks(links, canManage) {
        if (links.length === 0) {
            return (
                <Typography variant="body2" color="textSecondary">
                    Aucune boutique PrestaShop n&apos;est rattachée à ce compte.
                </Typography>
            );
        }
        return (
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Magasin</TableCell>
                        <TableCell>État</TableCell>
                        <TableCell>Synchronisation</TableCell>
                        {canManage && <TableCell align="right">Actions</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {links.map((link) => (
                        <TableRow key={link.store_id}>
                            <TableCell>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>{link.shop_name || link.shop_ref}</Typography>
                                <Typography variant="caption" color="textSecondary">{link.store_id}</Typography>
                            </TableCell>
                            <TableCell>
                                <Chip label={stateLabel(link.state)} size="small" style={STATE_COLORS[link.state]} />
                            </TableCell>
                            <TableCell>{this.renderSync(link)}</TableCell>
                            {canManage && (
                                <TableCell align="right">
                                    {link.state !== 'active' && (
                                        <Button size="small" disabled={this.props.writing} onClick={() => this.handleRegenerate(link)}>
                                            Régénérer le code
                                        </Button>
                                    )}
                                    {link.state !== 'detached' && (
                                        <Button
                                            size="small"
                                            color="secondary"
                                            disabled={this.props.writing}
                                            onClick={() => this.setState({ detachTarget: link })}
                                        >
                                            Détacher
                                        </Button>
                                    )}
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    }

    render() {
        const { links, loading, error, userRole, writing } = this.props;
        const { createOpen, shops, shopsError, shopRef, legacyStoreId, codeShown, detachTarget, actionError } = this.state;
        const canManage = canManageEcomLinks(userRole);
        const showLegacy = canSetLegacyStoreId(userRole);
        const legacyInvalid = showLegacy && legacyStoreId.trim() !== '' && !isValidLegacyStoreId(legacyStoreId.trim());
        const list = links;

        return (
            <Box>
                <Typography variant="body2" color="textSecondary" paragraph>
                    Rattachez une boutique PrestaShop à un magasin de ce compte : générez un code ici, puis collez-le
                    dans le module MyJouleBox Connect de la boutique. Une fois rattachée, la boutique envoie ses
                    commandes et reçoit le stock de ce magasin.
                </Typography>

                {error && <Alert severity="error" style={{ marginBottom: 12 }}>{error}</Alert>}
                {actionError && <Alert severity="error" style={{ marginBottom: 12 }}>{actionError}</Alert>}

                {canManage && (
                    <Box mb={2}>
                        <Button variant="contained" color="primary" onClick={this.openCreate} disabled={writing}>
                            Jumeler une boutique
                        </Button>
                    </Box>
                )}

                {loading && list.length === 0 ? <CircularProgress size={24} /> : this.renderLinks(list, canManage)}

                {/* Création */}
                <Dialog open={createOpen} onClose={this.closeCreate} fullWidth maxWidth="sm">
                    <DialogTitle>Jumeler une boutique PrestaShop</DialogTitle>
                    <DialogContent>
                        {shopsError && <Alert severity="error" style={{ marginBottom: 12 }}>{shopsError}</Alert>}
                        {actionError && <Alert severity="error" style={{ marginBottom: 12 }}>{actionError}</Alert>}
                        <TextField
                            select
                            fullWidth
                            margin="normal"
                            label="Magasin"
                            value={shopRef}
                            onChange={(e) => this.setState({ shopRef: e.target.value })}
                        >
                            {shops.map((s) => (
                                <MenuItem key={s.shop_ref} value={s.shop_ref}>
                                    {s.name}{s.city ? ' — ' + s.city : ''}
                                </MenuItem>
                            ))}
                        </TextField>
                        {showLegacy && (
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Identifiant historique (legacy_store_id)"
                                helperText={legacyInvalid
                                    ? '3 à 64 caractères : minuscules, chiffres et _'
                                    : 'Optionnel. Reprend l\'identifiant d\'une boutique déjà en service (superadmin).'}
                                error={legacyInvalid}
                                value={legacyStoreId}
                                onChange={(e) => this.setState({ legacyStoreId: e.target.value })}
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeCreate}>Annuler</Button>
                        <Button color="primary" variant="contained" onClick={this.handleCreate} disabled={!shopRef || legacyInvalid || writing}>
                            Générer le code
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Code affiché une fois (création ou régénération) */}
                <Dialog open={!!codeShown} onClose={() => this.setState({ codeShown: null })} fullWidth maxWidth="sm">
                    <DialogTitle>{codeShown && codeShown.title}</DialogTitle>
                    <DialogContent>
                        {codeShown && <CodeView code={codeShown.code} expiresAt={codeShown.expiresAt} />}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => this.setState({ codeShown: null })}>Fermer</Button>
                    </DialogActions>
                </Dialog>

                {/* Détachement */}
                <Dialog open={!!detachTarget} onClose={() => this.setState({ detachTarget: null })}>
                    <DialogTitle>Détacher la boutique ?</DialogTitle>
                    <DialogContent>
                        <Typography>
                            La boutique {detachTarget && (detachTarget.shop_name || detachTarget.store_id)} n&apos;enverra plus
                            de commandes et ne recevra plus de stock, immédiatement. Pour la rattacher à nouveau, il
                            faudra un nouveau code.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => this.setState({ detachTarget: null })}>Annuler</Button>
                        <Button color="secondary" variant="contained" onClick={this.handleDetach} disabled={writing}>
                            Détacher
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }
}

function mapStateToProps(state) {
    const ecom = (state.settings && state.settings.integrations && state.settings.integrations.ecom) || {};
    return {
        links: ecom.links || [],
        loading: !!ecom.loading,
        error: ecom.error || null,
        writing: !!ecom.writing,
        userRole: state.auth && state.auth.user && state.auth.user.role,
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({ fetchEcomLinks, createEcomLink, regenerateEcomLinkCode, detachEcomLink }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(EcomStoreLinksForm);
